import { Button, FormControl, FormHelperText, FormLabel, Grid, MenuItem, Stack } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { kpiApi } from "@/api/kpiApi";
import { Field, Form, FormikProvider, useFormik } from "formik";
import { Select, TextField } from "formik-mui";
import * as _ from "lodash";
import * as yup from "yup";
import { useRef } from "react";

// edit infomation form
const editInformationFormValidationSchema = yup.object().shape({
    id: yup.number(),
    kpi_code: yup.string().nullable(),
    title: yup.string().required(),
    description: yup.string().nullable(),
    kpi_type_id: yup.number().required(),
    timeframe_id: yup.number().required(),
    data_unit: yup.string().nullable(),
    data_formula: yup.string().nullable(),
    data_related: yup.string().nullable(),
});

function transformKpiToEditInformationInitialValue(kpi) {
    let values = {
        id: kpi.id,
        kpi_code: kpi.kpi_code,
        title: kpi.title,
        description: kpi.description,
        kpi_type_id: kpi.kpi_type_id,
        timeframe_id: kpi.timeframe_id,
        data_unit: kpi.data_unit,
        data_formula: kpi.data_formula,
        data_related: kpi.data_related,
    };

    for (let [key, value] of Object.entries(values)) {
        values[key] = _.isNull(value) ? "" : value;
    }

    return values;
}

export default function KpiInformationForm(props) {
    const { kpi } = props;
    const isSubmitted = useRef(false);

    const kpiData = kpi?.data?.data ?? {};
    const kpiOptions = kpiApi.endpoints.getKpiOptions.useQuery();
    const typeOptions = kpiOptions.data?.data?.kpiTypes ?? [];
    const timeframeOptions = kpiOptions.data?.data?.timeframes ?? [];

    const [updateKpiInformation] = kpiApi.endpoints.updateKpiInformation.useMutation();

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: editInformationFormValidationSchema,
        initialValues: transformKpiToEditInformationInitialValue(kpiData),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await updateKpiInformation({
                    kpiId: kpiData?.id,
                    data: values,
                }).unwrap();
                isSubmitted.current = true;
            } catch (error) {
                // log error later
                // console.error(error);
                isSubmitted.current = false;
            } finally {
                setSubmitting(false);
            }
        },
    });
    const { touched, errors } = formik;

    return (
        <FormikProvider value={formik}>
            <Form>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <FormLabel>รหัส</FormLabel>
                            <Field component={TextField} name="kpi_code" />
                            <FormHelperText name="kpi_code" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm="auto">
                        <FormControl fullWidth>
                            <FormLabel>ID</FormLabel>
                            <Field component={TextField} name="id" disabled={true} />
                            <FormHelperText name="id" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl required fullWidth>
                            <FormLabel>ชื่อตัวชี้วัด</FormLabel>
                            <Field component={TextField} name="title" required />
                            <FormHelperText name="title" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl required fullWidth>
                            <FormLabel>ประเภทตัวชี้วัด</FormLabel>
                            <Field component={Select} name="kpi_type_id" required>
                                {typeOptions?.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.title}
                                    </MenuItem>
                                ))}
                            </Field>
                            <FormHelperText name="type_type_id" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl required fullWidth>
                            <FormLabel>กรอบเวลา</FormLabel>
                            <Field component={Select} name="timeframe_id" required>
                                {timeframeOptions?.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.title}
                                    </MenuItem>
                                ))}
                            </Field>
                            <FormHelperText name="timeframe_id" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl required fullWidth>
                            <FormLabel>หน่วยนับ</FormLabel>
                            <Field component={TextField} name="data_unit" required />
                            <FormHelperText name="data_unit" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>คำอธิบาย</FormLabel>
                            <Field component={TextField} name="description" multiline={true} minRows={3} />
                            <FormHelperText name="description" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>วิธีการคำนวณ</FormLabel>
                            <Field component={TextField} name="data_formula" multiline={true} minRows={3} />
                            <FormHelperText name="data_formula" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>ข้อมูลที่เกี่ยวข้อง</FormLabel>
                            <Field component={TextField} name="data_related" multiline={true} minRows={3} />
                            <FormHelperText name="data_related" touched={touched} errors={errors} />
                        </FormControl>
                    </Grid>
                </Grid>

                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginTop: 3 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formik.isSubmitting || !formik.dirty}
                    >
                        save
                    </Button>
                    <Button type="reset" disabled={formik.isSubmitting}>
                        reset
                    </Button>

                    {isSubmitted.current && !formik.dirty && <CheckCircleOutlineIcon color="success" />}
                </Stack>
            </Form>
        </FormikProvider>
    );
}
